/**
 * titulo-estilo.js — Estilo de los TITULARES: degradado a mano y palabra resaltada (F6 del plan de personalización
 * manual, 16-10-2026; decisiones D8-D11 y D13-D14 del dueño, hoja H «Me gusta todo»).
 *
 * Módulo PURO (sin DOM ni `window`), ES2019: lo usan `blocks/hero.js` (el titular de la portada se pinta en su
 * render) y `core/render.js` + `pages/viewer.js::compileSiteHtml` (el título de las demás secciones, la MISMA
 * inyección en los dos: gotcha 12). Se prueba en node (tests/_titulo_estilo.mjs).
 *
 * DATOS (props del bloque; sin ninguno, y sin palabras resaltadas, el bloque sale BYTE A BYTE como antes):
 *   · `titulo_estilo`    'degradado' | 'resalte' | 'liso'. Ausente: la portada lleva el degradado automático de
 *                        siempre (`.title-gradient`) y los títulos de sección van lisos.
 *   · `titulo_degradado` {desde, hasta, direccion}: colores `#rrggbb` y 'horizontal' | 'diagonal' | 'vertical'.
 *                        Lo que falte toma el valor de siempre (del texto a la marca mezclada, 120°). Los colores
 *                        se RESPETAN (D1: el aviso de contraste lo da el panel, no se corrige en silencio).
 *   · La PALABRA resaltada no es un prop: es marcado dentro del texto del titular,
 *     `<span class="n3i-resalte" data-resalte="marca|rotulador|cursiva|degradado">…</span>`, para que sobreviva a
 *     la edición, al tecleo animado, al saneo y a la traducción (plan §3.7). Puede haber varias.
 *
 * CÓMO SE PINTA (site.css, «ESTILO DEL TITULAR»). Variables en el `<h1>` de la portada o en el `<section>`:
 *   · degradado a mano: `--tit-desde`, `--tit-hasta`, `--tit-ang` (los lee la misma regla que el automático) y,
 *     en la portada, la clase `n3i-tit-mano` (inline-block sin tope de ancho y sin `balance`: las líneas se
 *     parten como hoy y el vertical va de la primera a la última, D14).
 *   · resaltes: la marca CRUDA del bloque (`--resalte-marca`; la de `safe()` sale casi negra en portadas
 *     claras, hallazgo 2 de la hoja H) y su final (`--resalte-fin`: el 2.º color o un tono vecino), medidas
 *     contra lo que el titular tiene DETRÁS (la foto con su velo o `bgColor`, como el final del degradado,
 *     gotcha 65): si una palabra no llega a 3:1 se mezcla con el color del texto lo justo (`--resalte-mix`) y la
 *     franja del rotulador baja su opacidad (`--rotulador-a`, 55 % de serie: D13). La cursiva usa la serif del
 *     sitio si la tiene (`--resalte-serif`) y si no Playfair Display (su `<link>` lo pide `enlacesDeCursiva`).
 */
import { wcagRatio, hexToRgb, rgbToHex, safe, secondaryOf, checkAndFixContrast } from './contrast.js';
import { esHex, mezclaSrgb, pesoMarcaDelTitular, fondosDelTitular, PESO_MARCA_TITULAR } from './foto-fondo.js';
import { fuenteSitioActual, esPresetDeFuente } from './typography.js';
import { anadirAtributos, RE_SECCION } from './tag.js';
import { ANIMADOS, idVigente, rangoDe, opacidadPintada, alfaMaxDe, parametrosDe } from '../animations/fondos/catalogo.js';
import { segundoColor } from '../animations/fondos/paleta.js';

export var ESTILOS_TITULO = ['degradado', 'resalte', 'liso'];
export var ESTILOS_RESALTE = ['marca', 'rotulador', 'cursiva', 'degradado'];
export var DIRECCIONES_TITULO = { horizontal: '90deg', diagonal: '120deg', vertical: '180deg' };
export var PROPS_TITULO = ['titulo_estilo', 'titulo_degradado'];

/** Contraste mínimo de un titular (AA de texto grande): el de la hoja H y el del final del degradado. */
export var CONTRASTE_TITULO = 3;
/** Opacidad de serie de la franja del rotulador (D13: la de la hoja H). */
export var ROTULADOR_ALFA = 55;
/** Desplazamiento del tono vecino (OKLCH) cuando no hay 2.º color: +40° de matiz y ±0,14 de luz. */
var VECINO_MATIZ = 40;
var VECINO_LUZ = 0.14;

/** Títulos de sección: todos menos la portada (su render pinta el suyo), el marco común de todas las páginas,
 *  la barra fija, el bloque IA (iframe) y el carrusel (un titular por diapositiva). Lista NEGRA (gotcha 29). */
export var TITULO_SECCION_EXCLUDE = ['hero', 'header', 'footer', 'cta-bar', 'ai-custom', 'carrusel'];

export function admiteTituloSeccion(tipo) {
    return TITULO_SECCION_EXCLUDE.indexOf(tipo) === -1;
}

var RE_HEX6 = /^#[0-9a-fA-F]{6}$/;
var RE_RESALTE = /\bdata-resalte="(marca|rotulador|cursiva|degradado)"/g;

function _hex(v) {
    return typeof v === 'string' && RE_HEX6.test(v.trim()) ? v.trim().toLowerCase() : '';
}

/** Un color de MARCA crudo (`#rgb` o `#rrggbb`) en `#rrggbb`, o ''. */
function _marcaCruda(v) {
    if (typeof v !== 'string') return '';
    var t = v.trim();
    if (/^#[0-9a-fA-F]{3}$/.test(t)) t = '#' + t[1] + t[1] + t[2] + t[2] + t[3] + t[3];
    return _hex(t);
}

/** Lo que dicen los props, saneado: {estilo: '' | id, desde: '' | hex, hasta: '' | hex, ang: '' | grados}. */
export function normalizarTitulo(props) {
    var p = props || {};
    var estilo = ESTILOS_TITULO.indexOf(p.titulo_estilo) !== -1 ? p.titulo_estilo : '';
    var d = (p.titulo_degradado && typeof p.titulo_degradado === 'object') ? p.titulo_degradado : {};
    var dir = Object.prototype.hasOwnProperty.call(DIRECCIONES_TITULO, d.direccion) ? d.direccion : '';
    return { estilo: estilo, desde: _hex(d.desde), hasta: _hex(d.hasta), ang: dir ? DIRECCIONES_TITULO[dir] : '' };
}

/** ¿Tiene el titular algo puesto a mano en los props? (el punto de «tocado» del botón). */
export function tieneTituloAMano(props) {
    var n = normalizarTitulo(props);
    return !!(n.estilo || n.desde || n.hasta || n.ang);
}

/** Estilos de resalte que aparecen en el HTML de un titular, sin repetir y en el orden del catálogo. */
export function resaltesDe(html) {
    if (typeof html !== 'string' || html.indexOf('data-resalte') === -1) return [];
    var vistos = {};
    var m;
    RE_RESALTE.lastIndex = 0;
    while ((m = RE_RESALTE.exec(html))) vistos[m[1]] = true;
    return ESTILOS_RESALTE.filter(function (e) { return vistos[e]; });
}

// --- OKLCH (el tono vecino de la marca; se calcula aquí y se emite en hex: nada de `oklch(from …)` en el CSS,
//     que en un navegador sin color relativo dejaría el degradado inválido y la palabra transparente) ---

function _lin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
function _gam(c) { return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; }

function _aOklch(hex) {
    var rgb = hexToRgb(hex);
    var r = _lin(rgb[0]), g = _lin(rgb[1]), b = _lin(rgb[2]);
    var l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    var m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    var s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    var L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
    var A = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
    var B = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
    return [L, Math.sqrt(A * A + B * B), Math.atan2(B, A) * 180 / Math.PI];
}

function _deOklch(L, C, H) {
    var a = C * Math.cos(H * Math.PI / 180), b = C * Math.sin(H * Math.PI / 180);
    var l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
    var m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
    var s = Math.pow(L - 0.0894841775 * a - 1.2914855480 * b, 3);
    return [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s].map(_gam);
}

/** El tono vecino de `marca`: +40° de matiz y 0,14 de luz hacia el lado del texto (más claro si el texto es
 *  claro). Fuera de sRGB se rebaja el croma, no se recorta canal a canal (no cambia el matiz). */
export function tonoVecino(marca, texto) {
    if (!esHex(marca)) return '';
    var o = _aOklch(marca);
    var t = esHex(texto) ? hexToRgb(texto) : [26, 26, 26];
    var claro = (t[0] * 299 + t[1] * 587 + t[2] * 114) / 1000 > 140;   // el criterio de la hoja H
    var L = Math.min(1, Math.max(0, o[0] + (claro ? VECINO_LUZ : -VECINO_LUZ)));
    var C = o[1];
    var rgb = _deOklch(L, C, o[2] + VECINO_MATIZ);
    for (var i = 0; i < 60 && rgb.some(function (c) { return c < -1e-4 || c > 1 + 1e-4; }); i++) {
        C *= 0.94;
        rgb = _deOklch(L, C, o[2] + VECINO_MATIZ);
    }
    return rgbToHex.apply(null, rgb.map(function (c) { return Math.round(Math.min(1, Math.max(0, c)) * 255); }));
}

// --- MEDIDAS ---

function _peor(color, fondos) {
    var m = null;
    for (var i = 0; i < fondos.length; i++) {
        var r = wcagRatio(color, fondos[i]);
        if (m === null || r < m) m = r;
    }
    return m === null ? 0 : m;
}

/** El mayor valor de `max` a 0 con el que `medir(k)` llega al mínimo; `null` si `max` ya llega. Si ninguno llega,
 *  el que más se acerca (nunca peor que el de serie, que también compite). */
function _mayorQueLlega(max, medir) {
    var mejor = max, mejorRatio = -1;
    for (var k = max; k >= 0; k--) {
        var r = medir(k);
        if (r >= CONTRASTE_TITULO) return k === max ? null : k;
        if (r > mejorRatio) { mejor = k; mejorRatio = r; }
    }
    return mejor === max ? null : mejor;
}

/** Peso de la marca (0-100) en la palabra en color de marca y en el degradado de una palabra: los dos extremos
 *  mezclados con el texto a ese peso llegan a 3:1 en la peor zona de `fondos`. */
export function pesoDeResalte(texto, extremos, fondos) {
    if (!esHex(texto) || !fondos || !fondos.length) return null;
    var cols = (extremos || []).filter(esHex);
    if (!cols.length) return null;
    return _mayorQueLlega(100, function (k) {
        return Math.min.apply(null, cols.map(function (c) { return _peor(mezclaSrgb(c, texto, k), fondos); }));
    });
}

/** Opacidad de la franja del rotulador (0-55): el texto sobre la franja (la marca a esa opacidad sobre cada
 *  fondo) llega a 3:1. */
export function alfaDeRotulador(texto, marca, fondos) {
    if (!esHex(texto) || !esHex(marca) || !fondos || !fondos.length) return null;
    return _mayorQueLlega(ROTULADOR_ALFA, function (a) {
        var m = null;
        for (var i = 0; i < fondos.length; i++) {
            var r = wcagRatio(texto, mezclaSrgb(marca, fondos[i], a));
            if (m === null || r < m) m = r;
        }
        return m;
    });
}

// --- EL FONDO ANIMADO ---
// Una palabra en color de marca sobre un fondo animado del MISMO color pierde contraste: en s24 de test222
// (`blobs-morph` al 40 %) el fondo detrás de la palabra iba de #fffaf2 (el `bgColor` que se medía) a #eed8be, y la
// palabra en degradado se quedaba en 2,53:1. Lo que un fondo animado puede llegar a teñir sale de su catálogo
// (`alfaMax` por polaridad, a opacidad 100, por la opacidad que se PINTA), pero `alfaMax` es el alfa de UNA llamada de
// dibujo y las formas se pisan: se cuentan DOS capas superpuestas, 1 − (1 − a)² (en s24, 24 % frente al ~25-27 %
// medido en el peor 5 % de los píxeles detrás de la palabra). Se mide también contra el fondo teñido así con cada
// color que PINTA el fondo, los mismos que le da `fondos/efectiva.js::configEfectiva`: el `color1` elegido a mano o la
// marca y, si el efecto lleva segundo color, `paleta.js::segundoColor` (del `--secondary` de la sección).

/** Alfa (0-1) de UNA capa del fondo animado de un bloque (`alfaMax` por la opacidad pintada); 0 si no tiene. */
function _capaDelFondoAnimado(raw, oscuro) {
    var id = idVigente(raw && raw.bg_animation);
    if (typeof id !== 'string' || ANIMADOS.indexOf(id) === -1) return 0;
    var cfg = (raw.bg_animation_config && typeof raw.bg_animation_config === 'object') ? raw.bg_animation_config : {};
    var op = cfg.opacity;
    if (op === undefined || op === null || op === '') {
        var r = rangoDe(id, 'opacity');
        op = r ? r.defecto : 100;
    }
    op = Number(opacidadPintada(id, op));
    if (!isFinite(op)) op = 100;
    var a = alfaMaxDe(id, oscuro);
    return a === null ? 0 : a * Math.max(0, Math.min(100, op)) / 100;
}

/** Techo del tinte (%): el peor medido detrás de una palabra en test222 (s24, ~25-27 % en el peor 5 %). Sin él, un
 *  fondo fuerte y disperso (`bokeh` al 45 % en s32, portada oscura) daba un tinte del 64 % y la palabra «en color de
 *  marca» se mezclaba al 26 % con el texto: casi blanca, cuando con la marca entera ya medía 6,15:1. */
export var TINTE_MAX = 25;

/** El tinte máximo (0-100) del fondo animado de un bloque: dos capas de su color superpuestas, con techo; 0 si no
 *  tiene. */
export function tinteDelFondoAnimado(raw, oscuro) {
    var capa = _capaDelFondoAnimado(raw, oscuro);
    return Math.min(TINTE_MAX, Math.round(100 * (1 - (1 - capa) * (1 - capa))));
}

/** Opacidad del texto fantasma (`.ghost-text` de site.css: el color del texto al 4 %). */
var GHOST_ALFA = 4;

/** Los fondos contra los que se mide una palabra resaltada: los del titular y, con fondo animado, el PEOR caso de
 *  lo que se apila detrás (s24 de test222, medido: marca en dos capas, el segundo color encima y el texto fantasma). */
export function fondosParaResalte(raw, fondos, marca, texto) {
    var base = (fondos || []).filter(esHex);
    if (!base.length) return base;
    var bg = hexToRgb(base[0]);
    var oscuro = (bg[0] * 299 + bg[1] * 587 + bg[2] * 114) / 1000 <= 140;
    var t = tinteDelFondoAnimado(raw, oscuro);
    if (!t) return base;
    var cfg = (raw.bg_animation_config && typeof raw.bg_animation_config === 'object') ? raw.bg_animation_config : {};
    var c1 = _marcaCruda(cfg.color1) || marca;
    var colores = [c1];
    if (parametrosDe(idVigente(raw.bg_animation)).indexOf('color2') !== -1) {
        colores.push(segundoColor({ c1: c1, c2Mano: cfg.color2, c2Marca: secondaryOf(raw), oscuro: oscuro }));
    }
    var capa = Math.min(TINTE_MAX, Math.round(100 * _capaDelFondoAnimado(raw, oscuro)));
    var fantasma = !!(raw.ghost_text && esHex(texto));
    var out = base.slice();
    base.forEach(function (f) {
        var pila = mezclaSrgb(colores[0], f, t);
        out.push(pila);
        if (colores[1]) {
            out.push(mezclaSrgb(colores[1], f, t));
            pila = mezclaSrgb(colores[1], pila, capa);
            out.push(pila);
        }
        if (fantasma) out.push(mezclaSrgb(texto, pila, GHOST_ALFA));
    });
    return out;
}

// --- LA CURSIVA ---

/** Familias serif del catálogo de fuentes, con la URL de su itálica 500 en Google Fonts. Lujo (Marcellus, sin
 *  itálica) usa la de su texto. Fuera de la lista: Playfair Display. */
var SERIF_DE_PRESET = {
    editorial: 'Cormorant Garamond', warm: 'Lora', classic: 'Libre Baskerville', elegant: 'Playfair Display',
    artisan: 'Fraunces', luxe: 'Cormorant Garamond',
};
var CURSIVA_URL = {
    'Playfair Display': 'Playfair+Display:ital,wght@1,500',
    'Cormorant Garamond': 'Cormorant+Garamond:ital,wght@1,500',
    'Lora': 'Lora:ital,wght@1,500',
    'Libre Baskerville': 'Libre+Baskerville:ital@1',
    'Fraunces': 'Fraunces:ital,wght@1,500',
};
export var CURSIVA_POR_DEFECTO = 'Playfair Display';

function _presetDe(props) {
    var p = props || {};
    if (esPresetDeFuente(p.fuente)) return p.fuente;          // la de la sección («Texto de la sección», F4)
    var sitio = fuenteSitioActual();
    if (sitio) return sitio;
    return typeof p.fontFamily === 'string' ? p.fontFamily.toLowerCase().replace(/['"]/g, '').trim() : '';
}

/** La familia de la cursiva de un bloque: la serif de su fuente (la de la sección, F4, o la del sitio) o la de
 *  serie. */
export function serifDeCursiva(props) {
    var id = _presetDe(props);
    return Object.prototype.hasOwnProperty.call(SERIF_DE_PRESET, id) ? SERIF_DE_PRESET[id] : CURSIVA_POR_DEFECTO;
}

// --- LAS VARIABLES ---

/**
 * Declaraciones de un titular como {variable: valor}, en orden. Vacío si no hay nada puesto.
 * @param {Object} raw     props crudos del bloque (la marca y el 2.º color salen de aquí)
 * @param {Object} p       los mismos tras `safe()` (texto y acento ya contrastados)
 * @param {string[]} fondos lo que el titular tiene detrás (hex): `fondosDelTitular` en la portada, `bgColor` aquí
 * @param {string} titulo  HTML del titular (las palabras resaltadas)
 */
export function variablesTitulo(raw, p, fondos, titulo) {
    var n = normalizarTitulo(raw);
    var out = {};
    if (n.desde) out['--tit-desde'] = n.desde;
    if (n.hasta) out['--tit-hasta'] = n.hasta;
    if (n.ang) out['--tit-ang'] = n.ang;
    var res = resaltesDe(titulo);
    if (!res.length) return out;
    var texto = esHex(p && p.textColor) ? p.textColor.trim() : '';
    var marca = _marcaCruda(raw && raw.accentColor) || (esHex(p && p.accentColor) ? p.accentColor.trim() : '');
    if (!marca) return out;
    out['--resalte-marca'] = marca;
    var fin = '';
    if (res.indexOf('degradado') !== -1) {
        // El 2.º color CRUDO, como la marca (no el de `secondaryOf`, ya oscurecido contra el fondo): lo que lo hace
        // legible es la mezcla con el texto, que se mide abajo con los dos extremos (hoja H, s24).
        fin = _marcaCruda(raw && (raw.secondaryColor || raw.secondary_color)) || tonoVecino(marca, texto);
        if (fin) out['--resalte-fin'] = fin;
    }
    var fondosR = fondosParaResalte(raw || {}, fondos, marca, texto);
    var extremos = [];
    if (res.indexOf('marca') !== -1) extremos.push(marca);
    if (fin) extremos.push(marca, fin);
    var mix = extremos.length ? pesoDeResalte(texto, extremos, fondosR) : null;
    if (mix !== null) out['--resalte-mix'] = mix + '%';
    if (res.indexOf('rotulador') !== -1) {
        var a = alfaDeRotulador(texto, marca, fondosR);
        if (a !== null) out['--rotulador-a'] = a + '%';
    }
    if (res.indexOf('cursiva') !== -1) {
        var serif = serifDeCursiva(raw);
        if (serif !== CURSIVA_POR_DEFECTO) out['--resalte-serif'] = "'" + serif + "'";
    }
    return out;
}

/** Las declaraciones como texto para un `style` (`--x:v;`), o ''. */
export function estiloDeVariables(v) {
    return Object.keys(v).map(function (k) { return k + ':' + v[k] + ';'; }).join('');
}

/**
 * Lo que necesita el `<h1>` de la PORTADA (lo llama blocks/hero.js con lo que ya calcula para el degradado de
 * siempre): {degradado: bool (clase `title-gradient`), mano: bool (clase `n3i-tit-mano`), estilo: '' | texto}.
 * @param {boolean} automatico el degradado de siempre (acento distinto del texto)
 */
export function tituloDePortada(raw, p, fondos, automatico) {
    var n = normalizarTitulo(raw);
    var degradado = n.estilo ? n.estilo === 'degradado' : !!automatico;
    var vars = variablesTitulo(raw, p, fondos, raw && raw.title);
    if (!degradado) { delete vars['--tit-desde']; delete vars['--tit-hasta']; delete vars['--tit-ang']; }
    return { degradado: degradado, mano: degradado && !!(n.desde || n.hasta || n.ang), estilo: estiloDeVariables(vars), vars: vars };
}

/** Lo que el título de una SECCIÓN lleva en su `<section>`: {degradado: bool (`data-titulo`), vars}. */
export function variablesDeSeccion(block) {
    var raw = (block && block.props) || {};
    var n = normalizarTitulo(raw);
    if (!n.estilo && !resaltesDe(raw.title).length) return { degradado: false, vars: {} };
    var p = safe(raw, block.type);
    var fondos = esHex(p.bgColor) ? [p.bgColor.trim()] : [];
    var degradado = n.estilo === 'degradado';
    var vars = variablesTitulo(raw, p, fondos, raw.title);
    if (!degradado) { delete vars['--tit-desde']; delete vars['--tit-hasta']; delete vars['--tit-ang']; }
    else if (!n.hasta) {
        var peso = pesoMarcaDelTitular(p.textColor, secondaryOf(raw) || p.accentColor, fondos);
        if (peso !== null) vars['--title-mix'] = peso + '%';
    }
    return { degradado: degradado, vars: vars };
}

/**
 * Mete el estilo del título en el `<section>` de una sección (render.js y compileSiteHtml): `data-titulo` +
 * las variables. La portada no pasa por aquí. Sin nada puesto devuelve el HTML tal cual.
 */
export function inyectarTituloSeccion(html, block) {
    if (typeof html !== 'string' || !block || !admiteTituloSeccion(block.type)) return html;
    var s = variablesDeSeccion(block);
    var degradado = s.degradado;
    var decl = estiloDeVariables(s.vars);
    if (decl) {
        // DENTRO del style (como «Texto de la sección»): la regex no cruza un `>`, falla sin escribir.
        html = /<section\b[^>]*?style="/.test(html)
            ? html.replace(/(<section\b[^>]*?style=")/, '$1' + decl)
            : anadirAtributos(html, RE_SECCION, ' style="' + decl + '"');
    }
    // El atributo, con el cierre del tag respetando comillas (utils/tag.js, BAJO-3).
    return degradado ? anadirAtributos(html, RE_SECCION, ' data-titulo="degradado"') : html;
}

/** Familias de cursiva que usa un sitio (alguna palabra resaltada en cursiva), sin repetir: el `<head>`
 *  publicado pide su itálica. */
export function cursivasDelSitio(siteData) {
    var vistas = [];
    var pages = (siteData && Array.isArray(siteData.pages)) ? siteData.pages : [];
    pages.forEach(function (pg) {
        ((pg && Array.isArray(pg.blocks)) ? pg.blocks : []).forEach(function (b) {
            var t = b && b.props && b.props.title;
            if (resaltesDe(t).indexOf('cursiva') === -1) return;
            var fam = serifDeCursiva(b.props);
            if (vistas.indexOf(fam) === -1) vistas.push(fam);
        });
    });
    return vistas;
}

/** `<link>` de Google Fonts de esas itálicas (el mismo formato que el de la fuente del sitio). */
export function enlacesDeCursiva(familias) {
    return (familias || []).map(function (f) {
        var u = Object.prototype.hasOwnProperty.call(CURSIVA_URL, f) ? CURSIVA_URL[f] : '';
        return u ? '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=' + u + '&display=swap">\n' : '';
    }).join('');
}

/** La URL de la itálica de una familia (el editor la descarga al pintar), o ''. */
export function urlDeCursiva(familia) {
    return Object.prototype.hasOwnProperty.call(CURSIVA_URL, familia)
        ? 'https://fonts.googleapis.com/css2?family=' + CURSIVA_URL[familia] + '&display=swap' : '';
}

// --- PARA EL PANEL «Estilo del titular» (editor/titulo-panel.js) ---

/** ¿Tiene este bloque un titular con estilo? La portada y los títulos de sección. */
export function admiteEstiloDeTitulo(tipo) {
    return tipo === 'hero' || admiteTituloSeccion(tipo);
}

/** Lo que el titular de un bloque enseña AHORA: 'degradado' | 'resalte' | 'liso' (sin prop: la portada en
 *  degradado si su acento no es su texto; una sección, «resalte» si tiene palabras resaltadas y si no «liso»). */
export function estiloVisible(block) {
    var raw = (block && block.props) || {};
    var n = normalizarTitulo(raw);
    if (n.estilo) return n.estilo;
    if (block && block.type === 'hero') {
        var p = safe(raw, 'hero');
        if (p.accentColor && p.accentColor !== p.textColor) return 'degradado';
    }
    return resaltesDe(raw.title).length ? 'resalte' : 'liso';
}

/** Lo que el titular tiene DETRÁS (hex): la foto con su velo en la portada (`fondosDelTitular`), si no `bgColor`. */
export function fondosDeBloque(block) {
    var raw = (block && block.props) || {};
    var p = safe(raw, block && block.type);
    if (block && block.type === 'hero') return fondosDelTitular(p, 'hero').filter(esHex);
    return esHex(p.bgColor) ? [p.bgColor.trim()] : [];
}

/** Los colores del degradado tal como se ven sin elegir nada: del texto a la marca mezclada (el 62 % o el peso
 *  medido), y la dirección de siempre (diagonal). Son los valores con los que abre el panel. */
export function degradadoDeSerie(block) {
    var raw = (block && block.props) || {};
    var p = safe(raw, block && block.type);
    var texto = esHex(p.textColor) ? p.textColor.trim().toLowerCase() : '#1a1a1a';
    var marca = secondaryOf(raw) || p.accentColor;
    var peso = pesoMarcaDelTitular(texto, marca, fondosDeBloque(block));
    var hasta = esHex(marca) ? mezclaSrgb(marca, texto, peso === null ? PESO_MARCA_TITULAR : peso) : texto;
    return { desde: texto, hasta: hasta, direccion: 'diagonal' };
}

/** Contraste del PEOR extremo del degradado contra los fondos. */
export function contrasteDelDegradado(desde, hasta, fondos) {
    var cols = [desde, hasta].filter(esHex);
    if (!cols.length || !fondos || !fondos.length) return null;
    return Math.min.apply(null, cols.map(function (c) { return _peor(c, fondos); }));
}

/** «Arreglar» un extremo: el tono MÁS CERCANO (mismo matiz, otra luz) que llega a 3:1 en todas las zonas; si
 *  ninguno de ese matiz llega, el que más se acerca. Se MIDE cada candidato (gotcha 45). */
export function extremoArreglado(color, fondos) {
    if (!esHex(color) || !fondos || !fondos.length || _peor(color, fondos) >= CONTRASTE_TITULO) return color;
    var mejor = color, mejorR = _peor(color, fondos);
    var cands = fondos.map(function (f) { return checkAndFixContrast(f, color, CONTRASTE_TITULO); });
    for (var i = 0; i < cands.length; i++) {
        var c = _hex(cands[i]) || _marcaCruda(cands[i]);
        if (!c) continue;
        var r = _peor(c, fondos);
        if (r >= CONTRASTE_TITULO) return c;
        if (r > mejorR) { mejor = c; mejorR = r; }
    }
    return mejor;
}
